import React, { useMemo, useRef } from 'react';
import i18n from 'utils/i18n';
import { useTranslation } from 'react-i18next';
import { View, Document, Page, Text, Image } from '@react-pdf/renderer';
import GlassPreviewImage from 'i/glass.jpg';
import { getCurrentDate, getPrevDayNextMonth } from 'utils';
import { CONFIGURATION_TYPE } from 'utils/constant';
import { PdfHeader } from '../PdfHeader';
import { PdfBlockHeading } from '../PdfBlockHeading';
import {
	PdfGlassConfigDetailFirst,
	PdfGlassConfigDetailSecond,
	PdfGlassConfigTable,
} from '../PdfGlassConfig';
import { styles } from '../styles';

const splitPositions = (positions, firstPageRows, subsequentPageRows) => {
	if (positions.length <= firstPageRows) {
		return [positions];
	}
	const chunks = [];
	let start = 0;
	let pageRows = firstPageRows;
	while (start < positions.length) {
		const end = start + pageRows;
		chunks.push(positions.slice(start, end));
		start = end;
		pageRows = subsequentPageRows;
	}
	return chunks;
};

const PdfGlassDocument = ({
	glassData,
	totalValues,
	contactInfo,
	customerInfo,
	oversizedGlassFields,
	showPrice,
	priceVolumeCat,
	dueDate,
	uiHandlerRef,
}) => {
	const { t } = useTranslation('pdfPage');
	const pdfCreationDate = useRef(getCurrentDate());
	const priceValidUntilDate = useRef(getPrevDayNextMonth());

	const validUntil = t('priceValidUntilFmt').replace('{}', priceValidUntilDate.current);
	const priceValidUntilText = showPrice ? validUntil : null;
	const datasheets = useMemo(
		() => [
			glassData &&
				uiHandlerRef.current.glassDB.getDatasheet(glassData.flatGlassStructure, i18n.language),
		],
		[glassData, uiHandlerRef]
	);

	const allPages = useMemo(() => {
		const firstPageRows = 5;
		const subsequentPageRows = 20;

		const emptyRow = row =>
			row.every(
				item =>
					!(
						(item.name === 'length' || item.name === 'width' || item.name === 'quantity') &&
						item.value !== ''
					)
			);
		const positions = glassData.positions?.filter(o => !emptyRow(o.row)) || [];
		const chunks = splitPositions(positions, firstPageRows, subsequentPageRows);
		return chunks.map((chunk, chunkIndex) => {
			const startIndex =
				chunkIndex === 0 ? 0 : firstPageRows + (chunkIndex - 1) * subsequentPageRows;
			return {
				chunk,
				startIndex,
				isFirstPage: chunkIndex === 0,
				isLastPage: chunkIndex === chunks.length - 1,
			};
		});
	}, [glassData]);

	return (
		<Document style={styles.PDFContainer}>
			{allPages.map((pageInfo, pageIndex) => {
				const { chunk, startIndex, isFirstPage, isLastPage } = pageInfo;
				return (
					<Page style={styles.PDFContainer} size="A3" key={pageIndex}>
						<View style={styles.section}>
							<View style={styles.sectionBlock}>
								<PdfHeader
									documentType={t(CONFIGURATION_TYPE.drafts)}
									creationDate={pdfCreationDate.current}
									priceValidUntilText={priceValidUntilText}
									dueDateText={dueDate}
									contactInfo={contactInfo}
									customerInfo={customerInfo}
								/>
							</View>
							{isFirstPage && (
								<View style={styles.sectionBlock}>
									<View style={styles.glassConfigImageWlgState}>
										<Image
											src={glassData.screenshot ?? GlassPreviewImage}
											style={styles.glassConfigImage}
										/>
									</View>
									<PdfBlockHeading
										title={glassData.fireResistant}
										displayedBlockPosition={null}
										iconName="gear"
										datasheetUrl={datasheets[0]}
									/>
									<PdfGlassConfigDetailFirst configDetails={glassData} />
									<PdfGlassConfigDetailSecond
										configDetails={glassData}
										showPrice={showPrice}
										priceVolumeCat={priceVolumeCat}
									/>
								</View>
							)}
							<View style={styles.sectionBlock}>
								<PdfGlassConfigTable
									positions={chunk}
									totalValues={totalValues}
									oversizedGlassFields={oversizedGlassFields}
									glassIndex={0}
									startIndex={startIndex}
									showTotal={isLastPage}
									showPrice={showPrice}
									priceVolumeCat={priceVolumeCat}
								/>
							</View>
							<View style={styles.sectionBlockFooter} fixed>
								<Text
									style={styles.sectionBlockFooterText}
									render={({ pageNumber, totalPages }) => {
										const created = pdfCreationDate.current;
										const paging = t('pageFooterFmt')
											.replace('{pageNum}', pageNumber)
											.replace('{numPages}', totalPages);
										return `${created} - ${paging}`;
									}}
									fixed
								/>
							</View>
						</View>
					</Page>
				);
			})}
		</Document>
	);
};

export default PdfGlassDocument;
